import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { RefreshCw, Linkedin, Mail, Database, Zap, ChevronRight, Activity } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const Stat = ({ label, value, testid }) => (
  <div className="bg-card border border-border rounded-xl p-3" data-testid={testid}>
    <div className="text-2xl font-bold tabular-nums">{value}</div>
    <div className="text-[0.65rem] text-muted-foreground uppercase tracking-wide mt-0.5">{label}</div>
  </div>
);

const ConnChip = ({ icon: Icon, label, ok, detail }) => (
  <div className="flex items-center gap-2 py-2">
    <Icon className="w-4 h-4 text-muted-foreground" />
    <span className="text-sm flex-1">{label}</span>
    <span className={`text-[0.65rem] font-medium ${ok ? "text-emerald-400" : "text-amber-400"}`}>{detail}</span>
  </div>
);

export default function MobileHome() {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [running, setRunning] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/dashboard"); setD(data); }
    catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const refresh = async () => { setBusy(true); await load(); setBusy(false); };

  const runNow = async () => {
    setRunning(true);
    try { await api.post("/sequencer/run-now"); toast.success("Sequencer triggered"); setTimeout(load, 1500); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not run sequencer"); }
    setRunning(false);
  };

  if (!d) return <div className="text-sm text-muted-foreground py-10 text-center" data-testid="m-home-loading">Loading…</div>;

  const out = d.outbound_24h || {};
  const pipe = d.pipeline || {};
  const conn = d.connections || {};
  const approvals = (d.approval_queue || []).length;
  const replies = (d.open_replies || []).length;

  return (
    <div className="space-y-5" data-testid="m-home">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold">Today</h1>
        <button onClick={refresh} disabled={busy} className="text-muted-foreground" data-testid="m-home-refresh">
          <RefreshCw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} />
        </button>
      </div>

      {/* Needs you */}
      <div className="space-y-2">
        <Link to="/app/m/inbox" className="flex items-center justify-between bg-card border border-border rounded-xl px-4 py-3 active:bg-muted/40" data-testid="m-home-inbox-link">
          <div>
            <div className="text-sm font-medium">Lead replies & approvals</div>
            <div className="text-[0.7rem] text-muted-foreground">{replies} replies · {approvals} approvals waiting</div>
          </div>
          <ChevronRight className="w-4 h-4 text-muted-foreground" />
        </Link>
        <Link to="/app/m/engage" className="flex items-center justify-between bg-card border border-border rounded-xl px-4 py-3 active:bg-muted/40" data-testid="m-home-engage-link">
          <div>
            <div className="text-sm font-medium">Page engagement queue</div>
            <div className="text-[0.7rem] text-muted-foreground">Review viral posts to comment on</div>
          </div>
          <ChevronRight className="w-4 h-4 text-muted-foreground" />
        </Link>
      </div>

      {/* Outbound 24h */}
      <div>
        <div className="text-[0.65rem] text-muted-foreground uppercase tracking-widest mb-2">Sent · last 24h</div>
        <div className="grid grid-cols-3 gap-2">
          <Stat label="Emails" value={out.emails || 0} testid="m-stat-emails" />
          <Stat label="Invites" value={out.invites || 0} testid="m-stat-invites" />
          <Stat label="DMs" value={out.dms || 0} testid="m-stat-dms" />
          <Stat label="Comments" value={out.comments || 0} testid="m-stat-comments" />
          <Stat label="Posts" value={out.posts || 0} testid="m-stat-posts" />
          <Stat label="Failures" value={out.failures || 0} testid="m-stat-failures" />
        </div>
      </div>

      {/* Pipeline */}
      <div>
        <div className="text-[0.65rem] text-muted-foreground uppercase tracking-widest mb-2">Pipeline</div>
        <div className="grid grid-cols-3 gap-2">
          <Stat label="New" value={pipe.new || 0} testid="m-pipe-new" />
          <Stat label="Replied" value={pipe.replied || 0} testid="m-pipe-replied" />
          <Stat label="Won → ERP" value={pipe.won_to_erp || 0} testid="m-pipe-won" />
        </div>
      </div>

      {/* Connections */}
      <div className="bg-card border border-border rounded-xl px-4 py-2 divide-y divide-border/60" data-testid="m-connections">
        <ConnChip icon={Linkedin} label="LinkedIn" ok={conn.linkedin?.status === "connected"}
          detail={conn.linkedin?.status === "connected" ? "Connected" : (conn.linkedin?.status || "—")} />
        <ConnChip icon={Mail} label="Microsoft 365" ok={["connected", "app_only"].includes(conn.m365?.status)}
          detail={conn.m365?.status === "app_only" ? "App-only" : (conn.m365?.status || "—")} />
        <ConnChip icon={Database} label="ERP mirror" ok={conn.erp?.status === "configured"}
          detail={conn.erp?.status === "configured" ? "Synced" : (conn.erp?.status || "—")} />
      </div>

      <button onClick={runNow} disabled={running}
        className="w-full flex items-center justify-center gap-2 bg-signal-500 text-background font-semibold rounded-xl py-3 active:opacity-90 disabled:opacity-60"
        data-testid="m-run-sequencer">
        {running ? <Activity className="w-4 h-4 animate-pulse" /> : <Zap className="w-4 h-4" />}
        {running ? "Running…" : "Run sequencer now"}
      </button>
    </div>
  );
}
